import { forwardRef } from 'react';

import useForkRef from '@material-ui/core/utils/useForkRef';

import clsx from 'clsx';

import type { OverridableComponent, OverrideProps } from '@coursera/cds-common';

import type { ButtonProps } from '@core/Button';
import Button from '@core/Button';
import {
  useItemIndexInCollection,
  useRegisterItemInCollection,
} from '@core/utils';

import { classes } from './getActionButtonGroupCss';

export type CommonProps = {
  itemsVisibility: Record<string, boolean>;
  size?: 'small' | 'medium';
  variant?: 'secondary' | 'ghost' | 'ghostInvert';
};

export interface ActionButtonTypeMap<D extends React.ElementType = 'button'> {
  props: Omit<ButtonProps, 'variant' | 'size'>;
  defaultComponent: D;
}

export type Props<
  D extends React.ElementType = ActionButtonTypeMap['defaultComponent']
> = OverrideProps<ActionButtonTypeMap<D>, D> & {
  component?: React.ElementType;
};

const ActionButton: OverridableComponent<ActionButtonTypeMap> = forwardRef<
  HTMLButtonElement,
  Props
>(function ActionButton(props, ref) {
  const { ref: collectionRef, id, commonProps } = useRegisterItemInCollection<
    HTMLButtonElement,
    CommonProps,
    Props
  >(props, [props]);

  const indexInButtonGroup = useItemIndexInCollection(id);

  return (
    <Button
      ref={useForkRef(ref, collectionRef)}
      size={commonProps?.size}
      variant={commonProps?.variant || 'secondary'}
      {...props}
      className={clsx(props.className, {
        [classes.hiddenActionButton]:
          indexInButtonGroup !== 0 && !commonProps?.itemsVisibility[id],
      })}
      data-cds-overflow-id={id}
    />
  );
});

export default ActionButton;
